import { useEffect, useRef, useState } from "react";
import { ShoppingCart } from "react-feather";
import { useLocation, useNavigate } from "react-router-dom";
import { PlaceHolderStoreImage } from "../../assets/svgImages/svgImages";
import { useAuth } from "../../contexts/authContext";
import { productDetailsClient } from "../../data/product-details";
import { shopClient } from "../../data/shop";
import { OptionType } from "../../interfaces/option-type.interface";
import {
  Filters,
  ShopProduct,
  StoreDetails,
} from "../../interfaces/shop-products.interface";
import { NotFound } from "../../pages";
import { getAuthCredentials } from "../../utils/auth-utils";
import { SHOPPAGECONSTANTS } from "../../utils/shop-constants";
import { calculateProductsInCart, getSlugFromUrl } from "../../utils/utils";
import LoaderPage from "../command/loader/loader";
import { toast } from "react-toastify";
import "./shopComponent.scss";

const sortOptionList: OptionType[] = [
  {
    value: "desc",
    sortKey: "productName",
    label: SHOPPAGECONSTANTS.SORTOPTIONS.PRODUCTNAMEDESCENDING,
  },
  {
    value: "asc",
    sortKey: "productName",
    label: SHOPPAGECONSTANTS.SORTOPTIONS.PRODUCTNAMEASCENDING,
  },
];

const ShopComponent = () => {
  const location = useLocation();
  const [productList, setProductList] = useState<ShopProduct[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [storeUuid, setStoreUuid] = useState<string>('');
  const [hasMore, setHasMore] = useState(true);
  const [storeDetails, setStoreDetails] = useState({} as StoreDetails);
  const { quoteId } = getAuthCredentials();
  const [error, setError] = useState<string | null>(null);
  const [sortOptionsArray, setSortOptionsArray] =
    useState<OptionType[]>(sortOptionList);
  const [selectedSortOption, setSelectedSortOption] =
    useState<OptionType | null>({
      value: "desc",
      sortKey: "createdAt",
      label: SHOPPAGECONSTANTS.SORTOPTIONS.CREATEDATDESCENDING,
    });
  const [isSortOpen, setIsSortOpen] = useState(false); // State for sliding div
  const sortRef = useRef<HTMLDivElement>(null);
  const { shopPageSearchValue } = useAuth();
  const navigate = useNavigate();
  const [loadMore, setLoadMore] = useState(false);

  const toggleSort = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevents event from reaching the document listener
    setIsSortOpen((prev) => !prev);
  };

  // Close sort panel on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sortRef.current &&
        !sortRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest(".sortby") // Ensure clicking on the button doesn't trigger close
      ) {
        setIsSortOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const openProductDetails = (product: any) => {
    navigate(`/product/?id=${product.uuid}`);
  };

  const getStoreProducts = async (loadMore = false) => {
    if (isLoading || (loadMore && !hasMore)) return;

    setIsLoading(true);
    setLoadMore(loadMore);

    let lastProductName = "";

    if (
      selectedSortOption?.sortKey ===
        SHOPPAGECONSTANTS.SHOPPAGINATIONCONSTANTS.PRODUCTNAME &&
      loadMore
    ) {
      lastProductName = productList[productList.length - 1].productName;
    }

    const requestObject = {
      perPage: SHOPPAGECONSTANTS.SHOPPAGINATIONCONSTANTS.SHOP_PRODUCTS_PER_PAGE,

      ...(selectedSortOption?.sortKey ===
        SHOPPAGECONSTANTS.SHOPPAGINATIONCONSTANTS.CREATEDAT && {
        lastCreatedAt: loadMore ? cursor ?? undefined : undefined,
      }), // Only use cursor when loading more

      ...(selectedSortOption?.sortKey ===
        SHOPPAGECONSTANTS.SHOPPAGINATIONCONSTANTS.PRODUCTNAME && {
        lastProductName: loadMore ? lastProductName : undefined,
      }), // Only use cursor when loading more

      searchTerm: shopPageSearchValue ?? "",

      orderBy: selectedSortOption?.sortKey ?? "createdAt",

      sortedBy: selectedSortOption?.value ?? "desc",

      storeUuid: localStorage.getItem("storeUuid") ?? "",

      filters: {} as Filters,
    };

    const response = await shopClient.getStoreProducts(requestObject);

    if (response.success) {
      setProductList((prev) =>
        loadMore
          ? [...prev, ...response.data.productsList]
          : response.data.productsList
      );

      setStoreDetails(response.data.storeDetails);

      if (response.data.productsList.length > 0) {
        const lastItem =
          response.data.productsList[response.data.productsList.length - 1];

        if (response.data.hasMore) {
          setCursor(lastItem.createdAt); // Set cursor only if hasMore is true
          setHasMore(true);
        } else {
          setHasMore(false);
          if (!shopPageSearchValue) {
            setCursor(lastItem.createdAt); // When search is empty, set cursor if hasMore is true
          } else {
            setCursor(null); // Reset cursor if search term exists and no more results
          }
        }
      } else {
        setHasMore(false);
        setCursor(null); // Reset cursor when no results
      }
    } else {
      setHasMore(false);
      setCursor(null);
    }

    setIsLoading(false);
    setLoadMore(false);
    setIsSortOpen(false);
  };

  const getStoreFromSlug = async (slug: string) => {
    if (slug) {
      setIsLoading(true);
      const store = await shopClient.getStoreFromSlug(slug);
      console.log(store)
      if (store.success) {
        localStorage.setItem("storeUuid", store.data.uuid);
        setStoreUuid(store.data.uuid);
        getStoreProducts();
      } else {
        navigate("/not-found");
      }
    }
  };

  useEffect(() => {
    if (shopPageSearchValue) {
      setCursor(null); // Reset cursor only when new search happens
    }

    const url = window.location.origin + location.pathname;
    const slug = getSlugFromUrl(url) || null;

    if (slug) {      
      getStoreFromSlug(slug);
    } else {
      getStoreProducts();
    }

  }, [shopPageSearchValue, selectedSortOption]);

  useEffect(() => {
    if (quoteId && storeDetails) {
      productDetailsClient
        .getProductDetailsList({ quoteUuid: quoteId })
        .then((data: any) => {
          if (data.products.length > 0) {
            localStorage.setItem(
              "card-item",
              calculateProductsInCart(data?.products)
            );
          }
          setIsLoading(false);
        })
        .catch((error: any) => {
          toast.error(error.message);
          setError(error.message);
          setIsLoading(false);
        });
    }
  }, [quoteId, storeDetails]);

  if (isLoading && !loadMore) {
    return <LoaderPage />;
  }

  if (error) {
    <NotFound errorText="500" />;
  }

  if (!localStorage.getItem("storeUuid")) return <NotFound errorText="404" />;

  return (
    <div>
      <div className="store-wrapper">
        <div className="store-img">
          {/* <Image src='https://i.pinimg.com/736x/06/87/b9/0687b9e9693b8dd07f9cd6c36b2884e9.jpg' alt='store-image' className='img-fluid' /> */}
          {/* <Image src={!storeDetails.storeLogo ? storeDetails.storeLogo : './images/placeholder-store.svg'} alt='store-image' className='img-fluid' /> */}
          {/* <img src={imageSrc} alt='store-image' className='img-fluid' /> */}
          {storeDetails?.storeLogo ? (
            <img
              className="img-fluid"
              src={storeDetails?.storeLogo}
              alt="verify-identity-img"
            />
          ) : (
            <PlaceHolderStoreImage />
          )}
        </div>
        <div className="store-details">
          <div className="store-name">
            <h3>{storeDetails.storeName}</h3>
          </div>
          <p className="store-website">{storeDetails.storeWebUrl}</p>
        </div>
      </div>

      <div className="filter-option">
        <div className="sortby" onClick={(event) => toggleSort(event)}>
          <a href="#" onClick={(e) => e.preventDefault()}>
            <img src="/images/repeat.svg" />
            <span>Sort</span>{" "}
          </a>
        </div>
        {/* <div className="filter">
          <a href="#" onClick={(e) => e.preventDefault()}>
            <img src="./images/sliders.svg" />
            <span>Filter</span>{" "}
          </a>
        </div> */}

        {/* Sort Panel */}
        {isSortOpen && (
          <div ref={sortRef} className="sortby-option">
            {sortOptionsArray.map((option, index) => (
              <div className="sort-row" key={index}>
                <span
                  className="cursor-pointer"
                  onClick={() => {
                    setSelectedSortOption(option);
                  }}
                >
                  {option.label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="row g-4">
        {productList.length > 0 ? (
          productList.map((product) => (
            <div
              key={product.id}
              className="col-lg-3 col-md-4  col-6"
              onClick={() => openProductDetails(product)}
            >
              <div className="card product-card border-0 rounded-3 shadow-sm">
                <div className="position-relative">
                  <div className="overflow-hidden product-image-col">
                    {product.productImageUrl ? (
                      <img
                        className="card-img-top product-image rounded-0 rounded-md-3 rounded-bottom-0"
                        alt="Product Image"
                        src={product.productImageUrl}
                      />
                    ) : (
                      <PlaceHolderStoreImage />
                    )}
                  </div>
                </div>
                <div className="card-body p-3">
                  <h5 className="card-title mb-3 fw-medium">
                    {product.productName}
                  </h5>

                  <div className="d-flex justify-content-between align-items-center">
                    <span className="price">
                      {
                      product.productRentalPrice ? product.productRentalPriceDescription: 
                      <p className="d-flex align-items-center mb-0 availability-validation">Not available</p>}
                    </span>
                    <button
                      className="btn btn-outline-secondary btn-sm px-2 px-md-3 rounded-pill"
                      onClick={() => openProductDetails(product)}
                    >
                      <ShoppingCart size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div>No Products available to rent</div>
        )}
      </div>

      {hasMore && (
        <div className="d-flex justify-content-center">
          {" "}
          <button
            onClick={() => getStoreProducts(true)}
            disabled={isLoading}
            className="mt-5 btn btn-outline-secondary btn-sm px-3 rounded-pill"
          >
            {isLoading ? "Loading..." : "Load More"}
          </button>
        </div>
      )}
    </div>
  );
};

export default ShopComponent;
